import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { CheckCircle2, XCircle, AlertTriangle, Trophy } from 'lucide-react';
import GlossaryTerm from './GlossaryTerm';

/**
 * Mostra a colpo d'occhio quanto manca a una strategia per essere
 * promovibile a paper trading (4 soglie cap. 4 knowledge/rules/_master.md).
 *
 * Props:
 *  - summary: dict da currentSeries.summary (sharpe, calmar, max_dd, n_trades)
 *  - status: stringa dal frontmatter strategia (baseline|no_edge|promotable|...)
 */

const THRESHOLDS = [
  { key: 'sharpe', label: 'Sharpe', target: 1.0, dir: 'higher', fmt: (v) => v.toFixed(2) },
  { key: 'calmar', label: 'Calmar', target: 0.5, dir: 'higher', fmt: (v) => v.toFixed(2) },
  { key: 'max_dd', label: 'Max DD', target: -0.25, dir: 'higher', fmt: (v) => (v * 100).toFixed(1) + '%' },
  { key: 'n_trades', label: 'Trades', target: 5, targetMax: 200, dir: 'between', fmt: (v) => `${v}` },
];

const STATUS_TONE = {
  baseline: { bg: 'bg-blue-100 dark:bg-blue-900/40', text: 'text-blue-800 dark:text-blue-200', icon: Trophy, label: 'baseline' },
  no_edge: { bg: 'bg-amber-100 dark:bg-amber-900/40', text: 'text-amber-800 dark:text-amber-200', icon: XCircle, label: 'no edge' },
  edge_ok_portfolio_issue: { bg: 'bg-purple-100 dark:bg-purple-900/40', text: 'text-purple-800 dark:text-purple-200', icon: AlertTriangle, label: 'edge sì, portfolio issue' },
  promotable: { bg: 'bg-emerald-100 dark:bg-emerald-900/40', text: 'text-emerald-800 dark:text-emerald-200', icon: CheckCircle2, label: 'promovibile' },
  paper_trading: { bg: 'bg-cyan-100 dark:bg-cyan-900/40', text: 'text-cyan-800 dark:text-cyan-200', icon: Trophy, label: 'paper trading' },
  deprecated: { bg: 'bg-gray-200 dark:bg-gray-800', text: 'text-gray-700 dark:text-gray-300', icon: XCircle, label: 'deprecated' },
};

const passesThreshold = (value, t) => {
  if (value === null || value === undefined || Number.isNaN(value)) return null;
  if (t.dir === 'higher') return value >= t.target;
  if (t.dir === 'lower') return value <= t.target;
  if (t.dir === 'between') return value >= t.target && value <= t.targetMax;
  return null;
};

const formatGap = (value, t) => {
  if (value === null || value === undefined) return null;
  if (t.dir === 'higher') {
    if (value >= t.target) return null;
    const gap = t.target - value;
    return `manca ${t.fmt(gap)} per la soglia`;
  }
  if (t.dir === 'between') {
    if (value < t.target) return `serve ≥ ${t.target} trade per significatività`;
    if (value > t.targetMax) return `${value} trade > ${t.targetMax}: sospetto curve-fitting`;
    return null;
  }
  return null;
};

const PromotionGauge = ({ summary, status }) => {
  if (!summary) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-4 text-sm text-muted-foreground text-center">
          Seleziona una strategia per vedere la valutazione.
        </CardContent>
      </Card>
    );
  }

  const checks = THRESHOLDS.map((t) => ({
    ...t,
    value: summary[t.key],
    passes: passesThreshold(summary[t.key], t),
    gap: formatGap(summary[t.key], t),
  }));

  const nPass = checks.filter((c) => c.passes === true).length;
  const allPass = nPass === checks.length;
  const tone = STATUS_TONE[status] || STATUS_TONE.no_edge;
  const StatusIcon = tone.icon;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="text-sm flex items-center gap-2">
            <Trophy className="w-4 h-4 text-primary" />
            Strada verso paper trading
            <span className="font-mono text-xs text-muted-foreground">{nPass}/4 soglie</span>
          </CardTitle>
          {status && (
            <Badge className={`text-[10px] ${tone.bg} ${tone.text} flex items-center gap-1`}>
              <StatusIcon className="w-3 h-3" />
              {tone.label}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {checks.map((c) => {
            const passColor = c.passes === true ? 'border-emerald-500/50 bg-emerald-50 dark:bg-emerald-950/30'
                            : c.passes === false ? 'border-red-500/40 bg-red-50 dark:bg-red-950/20'
                            : 'border-muted bg-muted/20';
            const valColor = c.passes === true ? 'text-emerald-700 dark:text-emerald-300'
                           : c.passes === false ? 'text-red-700 dark:text-red-300'
                           : 'text-foreground';
            return (
              <div key={c.key} className={`rounded-lg border px-3 py-2 ${passColor}`}>
                <div className="flex items-center justify-between">
                  <GlossaryTerm term={c.key === 'max_dd' ? 'max_dd' : c.key}>
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{c.label}</p>
                  </GlossaryTerm>
                  {c.passes === true && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />}
                  {c.passes === false && <XCircle className="w-3.5 h-3.5 text-red-500" />}
                </div>
                <p className={`text-lg font-bold tabular-nums ${valColor}`}>
                  {c.value !== undefined && c.value !== null ? c.fmt(c.value) : '—'}
                </p>
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  Soglia: {c.dir === 'higher' ? '≥' : c.dir === 'between' ? `${c.target}–${c.targetMax}` : '≤'} {c.dir !== 'between' ? c.fmt(c.target) : ''}
                </p>
                {c.gap && (
                  <p className="text-[10px] text-red-600 dark:text-red-400 mt-1 leading-tight">{c.gap}</p>
                )}
              </div>
            );
          })}
        </div>
        {allPass && (
          <div className="mt-3 rounded-md bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-500/30 px-3 py-2 text-xs">
            <span className="font-medium text-emerald-700 dark:text-emerald-300">
              ✨ Tutte le soglie passate.
            </span>
            <span className="text-muted-foreground ml-1">
              Procedi a walk-forward → paper trading 6 mesi (cap. 4 _master.md).
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PromotionGauge;
